/* Joy product page: variant form, purchase bar and "Hemen Al", gallery and zoom, ring size row,
   personalisation and delivery estimate.
   Loaded by snippets/joy-product-assets.liquid in every section that shows a product form; it runs after
   assets/joy.js (in the head, deferred), which provides the shared helpers on window.Joy. */
(() => {
  'use strict';
  if (window.JoyProductLoaded || !window.Joy) return;
  window.JoyProductLoaded = true;
  const { root, T, cart, announce, request, parsed, updateCart, ConnectionError, maxQuantity } = window.Joy;
  const consentSelector = '[data-cart-consent] input[type="checkbox"]';

  // ÖBF/MSS approval: every copy on the page (product line, purchase bar, cart drawer) shows the same state.
  // joy.js saves the ticked box on the cart, so the other copies change without a change event.
  const syncConsent = (source) => {
    document.querySelectorAll(consentSelector).forEach((box) => {
      if (box === source) return;
      box.checked = source.checked;
      const wrap = box.closest('[data-cart-consent]');
      wrap.classList.remove('is-invalid');
      const error = wrap.querySelector('[data-consent-error]');
      if (error) error.hidden = true;
    });
    document.querySelectorAll('joy-product').forEach((product) => product.syncPurchase?.());
  };
  document.addEventListener('change', (event) => {
    const box = event.target.closest?.(consentSelector);
    if (box) syncConsent(box);
  });

  class JoyProduct extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.form = this.querySelector('.product-form');
      // An option is either a native select or a fieldset of radio chips.
      this.options = [...this.querySelectorAll('[data-option-position]')];
      this.options.forEach((option) => {
        if (option.matches('select')) option.required = true;
        else option.querySelectorAll('input[type="radio"]').forEach((radio) => { radio.required = true; });
      });
      this.requestNumber = 0;
      this.resolving = false;
      this.adding = false;
      this.purchaseOffscreen = false;
      this.syncPurchase();
      this.observePurchase();
      // Back from checkout (page restored from the back-forward cache): release the "Hemen Al" busy state.
      // The order confirmation closes too, so a tap can never add the piece a second time.
      window.addEventListener('pageshow', (event) => {
        if (!event.persisted) return;
        this.releasePurchase();
        this.querySelector('[data-buy-now-sheet]')?.close();
      }, { signal: this.controller.signal });
      // The page is really going to checkout: addToCart's fallback release is not needed.
      window.addEventListener('pagehide', () => clearTimeout(this.leaveTimer), { signal: this.controller.signal });
      // Order confirmation: "Vazgeç", the close icon or a tap outside the sheet closes it (Esc closes it natively).
      this.addEventListener('click', (event) => {
        const sheet = event.target.closest?.('[data-buy-now-sheet]');
        if (sheet && (event.target === sheet || event.target.closest('[data-sheet-close]'))) sheet.close();
      }, { signal: this.controller.signal });
      this.addEventListener('change', (event) => {
        if (event.target.closest('[data-option-position]')) this.resolveVariant();
        if (event.target.closest('[data-shape-option]')) this.syncModels();
        if (event.target.closest('[data-shape-option], [data-stone-option]')) this.showColour();
        const sizeBox = event.target.closest('[data-size-unknown]');
        const sizeNote = sizeBox?.querySelector('[data-size-note]');
        if (sizeNote) sizeNote.hidden = event.target.value !== sizeBox.dataset.sizeUnknown;
        const property = event.target.closest('[data-property-group]');
        if (property) {
          const chosen = property.querySelector('[data-property-chosen]');
          if (chosen) chosen.textContent = event.target.value;
          if (property.matches('[data-colour-property]')) this.showColour(event.target.value);
          if (property.matches('[data-model-group]')) this.showColour();
        }
      }, { signal: this.controller.signal });
      // The floating WhatsApp button sits outside the product (layout/theme.liquid); the page's first product fills it.
      document.addEventListener('click', (event) => {
        const float = event.target.closest?.('.whatsapp-float[data-whatsapp-config]');
        if (float && document.querySelector('joy-product') === this) this.fillWhatsapp(float);
      }, { signal: this.controller.signal });
      this.addEventListener('click', (event) => {
        const whatsapp = event.target.closest('[data-whatsapp-config]');
        if (whatsapp) this.fillWhatsapp(whatsapp);
        const thumbnail = event.target.closest('[data-show-media]');
        if (thumbnail) this.showMedia(thumbnail.dataset.showMedia);
        if (event.target.closest('[data-retry-variant]')) this.resolveVariant();
        if (event.target.closest('[data-gallery-prev]')) this.goTo(this.index - 1);
        if (event.target.closest('[data-gallery-next]')) this.goTo(this.index + 1);
        const zoomOpen = event.target.closest('[data-zoom-open]');
        if (zoomOpen) this.openZoom(Number(zoomOpen.dataset.zoomOpen));
        if (event.target.closest('[data-size-guide-open]')) this.querySelector('[data-size-guide]')?.showModal();
        if (event.target.closest('[data-size-guide-close]')) this.querySelector('[data-size-guide]')?.close();
      }, { signal: this.controller.signal });
      this.form?.addEventListener('submit', (event) => this.addToCart(event), { signal: this.controller.signal });
      this.setupGallery();
      this.setupZoom();
      this.setupStickyInformation();
      this.setupGems();
      this.setupModels();
      this.setupLetters();
      this.setupSizeFacts();
      this.querySelector('[data-size-guide]')?.addEventListener('click', (event) => {
        if (event.target === event.currentTarget) event.currentTarget.close();
      }, { signal: this.controller.signal });
    }
    // Coloured-stone versions are separate products: their links keep the ayar (same option values) and the gold
    // colour (?altin=…, applied here when the page opens).
    setupGems() {
      const links = [...this.querySelectorAll('[data-gem-link]')];
      const wanted = new URLSearchParams(location.search).get('altin');
      const colour = wanted && [...this.querySelectorAll('[data-colour-property] input')].find((input) => input.value === wanted);
      if (colour && !colour.checked) {
        colour.checked = true;
        colour.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (!links.length) return;
      const update = () => {
        const chosen = {};
        this.options.forEach((option) => {
          const input = option.matches('select') ? option.selectedOptions[0] : option.querySelector('input:checked');
          if (input?.dataset.value) chosen[option.dataset.optionName] = input.dataset.value;
        });
        const gold = this.querySelector('[data-colour-property] input:checked')?.value;
        links.forEach((link) => {
          let gem;
          try { gem = JSON.parse(link.dataset.gem); } catch (error) { return; }
          const match = gem.variants.find(([, values]) => gem.names.every((name, index) => chosen[name] === undefined || chosen[name] === values[index]));
          const url = new URL(gem.url, location.origin);
          if (match) url.searchParams.set('variant', match[0]);
          if (gold) url.searchParams.set('altin', gold);
          link.href = url.pathname + url.search;
        });
      };
      update();
      this.addEventListener('change', update, { signal: this.controller.signal });
    }
    // Designs made as separate products (the "Model" row) and the natural / lab-grown twin (the "Pırlanta" row): each
    // link opens the same length, ayar and gold colour, with the stone size whose total carat is nearest the chosen one
    // (the total carat is what sells these pieces). The twin link also shows its price for that match.
    setupModels() {
      const links = [...this.querySelectorAll('[data-model-link]')];
      if (!links.length) return;
      let facts = {};
      try { facts = JSON.parse(this.querySelector('[data-size-facts-data]')?.textContent || '{}') || {}; } catch (error) { facts = {}; }
      const total = (data, values) => {
        const stone = values.find((value) => data?.stones?.[value]);
        const sizes = data?.stones?.[stone]?.sizes || {};
        const size = values.find((value) => sizes[value]);
        return { stone, carat: sizes[size]?.ct };
      };
      const update = () => {
        const chosen = this.options
          .map((option) => (option.matches('select') ? option.selectedOptions[0] : option.querySelector('input:checked'))?.dataset.value)
          .filter(Boolean);
        const here = total(facts, chosen);
        const gold = this.querySelector('[data-colour-property] input:checked')?.value;
        links.forEach((link) => {
          let model;
          try { model = JSON.parse(link.dataset.model); } catch (error) { return; }
          // Every other option (length, ayar) as chosen when that design has it, then the nearest total carat.
          let best = null;
          (model.variants || []).forEach(([id, values, price]) => {
            const there = total(model.facts, values);
            const misses = values.filter((value) => value !== there.stone && !chosen.includes(value)).length;
            const gap = here.carat != null && there.carat != null ? Math.abs(there.carat - here.carat) : 0;
            const score = misses * 1000 + gap;
            if (!best || score < best.score) best = { id, score, price };
          });
          const url = new URL(model.url, location.origin);
          if (best) url.searchParams.set('variant', best.id);
          if (gold) url.searchParams.set('altin', gold);
          link.href = url.pathname + url.search;
          const price = link.querySelector('[data-twin-price]');
          if (price) price.textContent = best?.price || '';
        });
      };
      update();
      this.addEventListener('change', update, { signal: this.controller.signal });
    }
    // Gallery: native scroll-snap track (swipe on touch), arrows, counter, thumbnails and hover magnifier.
    setupGallery() {
      const options = { signal: this.controller.signal };
      this.track = this.querySelector('[data-gallery-track]');
      this.slides = [...this.querySelectorAll('.gallery-slide:not([hidden])')];
      this.index = 0;
      if (!this.track) return;
      let frame;
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => this.syncGallery());
      }, options);
      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.goTo(this.index - 1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); this.goTo(this.index + 1); }
      }, options);
      const activeId = this.querySelector('[data-variant-result]')?.dataset.mediaId;
      if (activeId) this.showMedia(activeId, 'instant');
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      let box = null;
      window.addEventListener('scroll', () => { box = null; }, { passive: true, signal: this.controller.signal });
      this.track.querySelectorAll('.gallery-zoom img').forEach((image) => {
        image.addEventListener('pointerenter', () => {
          box = null;
          image.sizes = '2000px';
          image.closest('.gallery-slide').classList.add('is-magnified');
        }, options);
        image.addEventListener('pointermove', (event) => {
          // The button's box: the photo itself is scaled while magnified.
          box ||= image.parentElement.getBoundingClientRect();
          image.style.transformOrigin = `${((event.clientX - box.left) / box.width) * 100}% ${((event.clientY - box.top) / box.height) * 100}%`;
        }, options);
        image.addEventListener('pointerleave', () => {
          image.closest('.gallery-slide').classList.remove('is-magnified');
        }, options);
      });
    }
    syncGallery() {
      if (!this.track || !this.slides.length) return;
      const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
      if (index === this.index && this.gallerySynced) return;
      this.gallerySynced = true;
      this.index = Math.max(0, Math.min(this.slides.length - 1, index));
      const counter = this.querySelector('[data-gallery-counter]');
      if (counter) {
        counter.textContent = `${this.index + 1} / ${this.slides.length}`;
        counter.hidden = this.slides.length < 2;
      }
      const media = this.slides[this.index]?.dataset.media;
      this.querySelectorAll('[data-show-media]').forEach((button) => {
        button.setAttribute('aria-current', String(Boolean(media) && button.dataset.showMedia === media));
      });
      // The active thumbnail scrolls into the strip's view (the Tektaş stone-on-hand one is the last of 25).
      const strip = this.querySelector('.gallery-thumbnails');
      const thumb = strip?.querySelector('[aria-current="true"]');
      if (thumb && strip.scrollWidth > strip.clientWidth) {
        const offset = thumb.getBoundingClientRect().left - strip.getBoundingClientRect().left;
        strip.scrollBy({ left: offset - (strip.clientWidth - thumb.offsetWidth) / 2, behavior: 'smooth' });
      }
      // Video posters held back in data-poster (snippets/joy-product-gallery.liquid) load for the shown slide and its
      // neighbours, so the next one is ready before it scrolls in.
      [this.index - 1, this.index, this.index + 1].forEach((position) => {
        this.slides[position]?.querySelectorAll('video[data-poster]').forEach((video) => {
          video.poster = video.dataset.poster;
          video.removeAttribute('data-poster');
        });
      });
      // The shown slide's video plays by itself, muted and looping (owner 2026-10-08), unless the visitor asks for less
      // motion; the other slides' videos pause.
      const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.slides.forEach((slide, position) => {
        slide.querySelectorAll('video').forEach((video) => {
          if (position !== this.index || still) {
            if (position !== this.index) video.pause();
            return;
          }
          video.muted = true;
          video.play()?.catch(() => { /* the browser refused: the play button stays */ });
        });
      });
    }
    // WhatsApp buttons: the message lists the customer's choices ("Kesim: Oval, Pırlanta: 1,00 ct · G · VS1 · GIA,
    // Ayar: 18 K, Altın Rengi: Beyaz Altın, Yüzük Ölçüsü: 14") and links the chosen variant. Filled in on press, so it
    // always matches the page; without choices the prefilled message from the theme stays.
    fillWhatsapp(link) {
      const choices = [];
      this.querySelectorAll('[data-option-name]').forEach((option) => {
        const value = option.matches('select')
          ? option.selectedOptions[0]?.dataset.value
          : option.querySelector('[data-value]:checked')?.dataset.value;
        if (value) choices.push(`${option.dataset.optionName}: ${value}`);
      });
      this.form?.querySelectorAll('[name^="properties["]').forEach((input) => {
        if (input.disabled || !input.value || (input.type === 'radio' && !input.checked)) return;
        const name = input.name.slice('properties['.length, -1);
        if (!name.startsWith('_')) choices.push(`${name}: ${input.value}`);
      });
      if (this.alyansSummary) choices.push(...this.alyansSummary().map(([name, value]) => `${name}: ${value}`));
      if (!choices.length) return;
      const url = new URL(this.dataset.productUrl, document.baseURI);
      const variantId = this.querySelector('[data-variant-result]')?.dataset.variantId;
      if (variantId) url.searchParams.set('variant', variantId);
      const message = link.dataset.whatsappConfig.replace('%CONFIG%', choices.join(', ')).replace('%URL%', url.href);
      const href = new URL(link.href);
      href.searchParams.set('text', message);
      link.href = href.href;
    }
    // Tektaş: joy-tektas.js shows the stone-on-hand slide on the two-column layout and hides it on phones.
    // Letter necklaces (option "Harf" / "Letter", owner 2026-10-09): the gallery shows the chosen letter's photos and
    // video (alt text "… · A Harfi"; a video by its id in joy.letters "v") with the photos of several letters, and the
    // line under the letters gives its gold weight and, on the stoned version, its stones; the specs follow.
    setupLetters() {
      const group = this.querySelector('[data-letter-option]');
      if (!group) return;
      let data = {};
      try { data = JSON.parse(group.querySelector('[data-letter-data]')?.textContent || '{}') || {}; } catch (error) { /* no facts */ }
      // The letter's video follows the letter's own photos (uploads land after the group shots).
      Object.entries(data).forEach(([letter, info]) => {
        if (!info?.v) return;
        this.querySelectorAll(`[data-media="${info.v}"], [data-show-media="${info.v}"]`).forEach((element) => {
          element.dataset.letter = letter;
          const photos = [...element.parentElement.children].filter((sibling) => sibling !== element && sibling.dataset.letter === letter);
          photos.at(-1)?.after(element);
        });
      });
      const facts = group.querySelector('[data-letter-facts]');
      const chosen = group.querySelector('[data-option-chosen]');
      const lang = document.documentElement.lang || 'tr';
      const number = (value) => Number(value).toLocaleString(lang, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const fill = (template, values) => String(template || '').replace(/\[(\w+)\]/g, (match, key) => values[key] ?? match);
      const specValue = (label) => [...this.querySelectorAll('.spec-list > div')]
        .find((row) => label && row.querySelector('dt')?.textContent.trim() === label)?.querySelector('dd');
      const goldSpec = specValue(facts?.dataset.goldLabel);
      const stoneSpec = specValue(facts?.dataset.stoneLabel);
      const apply = (letter, opening = false) => {
        if (!letter) return;
        if (chosen) chosen.textContent = letter;
        const info = data[letter] || {};
        const values = { letter, grams: info.g ? number(info.g) : '', carat: info.ct ? number(info.ct) : '', count: info.n || '' };
        if (facts) {
          const parts = [info.g && fill(facts.dataset.gold, values), info.ct && fill(facts.dataset.stones, values)].filter(Boolean);
          facts.textContent = parts.length ? `${fill(facts.dataset.lead, values)} ${parts.join(' · ')}` : '';
          facts.hidden = !parts.length;
          if (goldSpec && info.g) goldSpec.textContent = fill(facts.dataset.specGold, values);
          if (stoneSpec && info.ct) stoneSpec.textContent = stoneSpec.textContent.replace(/^[\d.,]+\s*ct(\s*tw)?/, fill(facts.dataset.specStone, values));
        }
        let changed = false;
        this.querySelectorAll('.gallery-slide[data-letter], .gallery-thumbnails [data-letter]').forEach((element) => {
          const hide = element.dataset.letter !== letter;
          if (element.hidden !== hide) {
            element.hidden = hide;
            changed = true;
          }
        });
        if (changed && this.track) {
          this.refreshGallery();
          // A chosen letter shows its own first photo straight away (group shots, e.g. JOY, when it has none); the page
          // opens on the first slide unless its link names a variant.
          const own = this.slides.findIndex((slide) => slide.dataset.letter === letter);
          const first = opening && !new URLSearchParams(location.search).has('variant') ? 0 : Math.max(0, own);
          this.goTo(first, 'instant');
        }
      };
      group.addEventListener('change', (event) => apply(event.target.dataset?.value), { signal: this.controller.signal });
      apply(group.querySelector('input:checked')?.dataset.value, true);
    }
    // Size-priced builders (product metafield joy.size_facts: Tamtur, Yarımtur, Su Yolu; owner 2026-10-09). Every
    // choice updates the title's total carat ("0,60 Karat Tamtur Pırlanta Yüzük": the carat sells these pieces), the
    // total carat on each stone-size chip at the chosen size, the line under the options (stones, carat, gold), the
    // "Toplam Karat" line on the order and, on rings, the band of stones on a hand (a gallery slide on the two-column
    // layout, like the Tektaş stone). Sizes the chosen stone size is not made in are greyed out.
    setupSizeFacts() {
      const line = this.querySelector('[data-size-facts]');
      if (!line) return;
      let data = {};
      try { data = JSON.parse(this.querySelector('[data-size-facts-data]')?.textContent || '{}') || {}; } catch (error) { return; }
      const lang = document.documentElement.lang || 'tr';
      const number = (value, digits = 2) => Number(value).toLocaleString(lang, { minimumFractionDigits: digits, maximumFractionDigits: digits });
      const fill = (template, values) => String(template || '').replace(/\[(\w+)\]/g, (match, key) => values[key] ?? match);
      const allSizes = new Set(Object.values(data.stones || {}).flatMap((stone) => Object.keys(stone.sizes || {})));
      const sizeOption = this.options.find((option) => [...option.querySelectorAll('[data-value]')].some((input) => allSizes.has(input.dataset.value)));
      const totalOption = this.querySelector('[data-total-option]');
      const totalInput = this.querySelector('[data-size-total]');
      const title = this.querySelector('h1[data-size-title]');
      const stickyTitle = this.querySelector('.sticky-product-title');
      const band = this.querySelector('[data-band-hand]');
      const handSlide = this.querySelector('[data-hand-slide]');
      const handThumb = this.querySelector('[data-hand-thumb]');
      const wide = window.matchMedia('(min-width: 750px)');
      let current = {};
      // The band on the hand photo: the ring finger is 19,65 % of the photo's width, taken as 17,5 mm (the Pırlanta
      // Rehberi's scale); the stones on the front half of the ring, narrower towards the sides as they turn away.
      // The round stone fills 50,2 % × 50 % of its photo (joy-tektas.js SHAPES.round).
      const FINGER_MM = 17.5;
      const METAL = { white: ['#bfc0bb', '#f1f1ee'], yellow: ['#b98f3e', '#efd594'], rose: ['#bd846c', '#f0c6b3'] };
      const metal = () => {
        const colour = (this.querySelector('[data-colour-property] input:checked')?.value || '').toLocaleLowerCase(lang);
        if (colour.includes('sarı') || colour.includes('yellow')) return METAL.yellow;
        if (colour.includes('pembe') || colour.includes('rose')) return METAL.rose;
        return METAL.white;
      };
      const drawBand = () => {
        const frame = band?.querySelector('.stone-hand-frame');
        const box = band?.querySelector('[data-band-stones]');
        const mm = Number(data.stones?.[current.stone]?.mm) || 0;
        if (!frame || !box || !mm || !frame.clientWidth) return;
        const width = frame.clientWidth;
        const height = frame.clientHeight;
        const px = (width * 0.1965) / FINGER_MM;
        const radius = FINGER_MM / 2;
        const step = (mm + 0.25) / radius;
        const count = Math.floor(Math.PI / step);
        const [shade, light] = metal();
        const x = width * 0.342;
        const y = height * 0.51;
        const strip = document.createElement('span');
        Object.assign(strip.style, {
          left: `${x - radius * px}px`, top: `${y - mm * 0.62 * px}px`, width: `${2 * radius * px}px`, height: `${mm * 1.24 * px}px`,
          borderRadius: `${mm * 0.3 * px}px`, background: `linear-gradient(90deg, ${shade}, ${light} 50%, ${shade})`,
        });
        const stones = Array.from({ length: count }, (_, index) => {
          const angle = (index - (count - 1) / 2) * step;
          const stoneWidth = (mm * Math.cos(angle) * px) / 0.502;
          const stoneHeight = (mm * px) / 0.5;
          const image = document.createElement('img');
          image.src = band.dataset.stoneSrc;
          image.alt = '';
          Object.assign(image.style, {
            left: `${x + radius * Math.sin(angle) * px - stoneWidth / 2}px`, top: `${y - stoneHeight / 2}px`,
            width: `${stoneWidth}px`, height: `${stoneHeight}px`, zIndex: String(Math.round(Math.cos(angle) * 100)),
          });
          return image;
        });
        box.replaceChildren(strip, ...stones);
      };
      const placeBand = () => {
        if (!band) return;
        if (wide.matches && handSlide) handSlide.append(band);
        else (totalOption || line).after(band);
        if (handSlide) handSlide.hidden = !wide.matches;
        if (handThumb) handThumb.hidden = !wide.matches;
        band.hidden = false;
        this.refreshGallery?.();
        drawBand();
      };
      // HRD report (owner + Kadir 2026-10-12, products tagged "HRD"): a service billed on its own line. The button shows
      // its price for the chosen total carat (variant SKU HRD-0430 = 4,30 ct), puts "HRD Raporu" on the piece's line,
      // adds the service line next to it (addToCart) and its days to the delivery estimate.
      const hrd = this.querySelector('[data-hrd-option]');
      const hrdToggle = hrd?.querySelector('[data-hrd-toggle]');
      let hrdVariants = {};
      try { hrdVariants = JSON.parse(hrd?.querySelector('[data-hrd-variants]')?.textContent || '{}') || {}; } catch (error) { hrdVariants = {}; }
      const syncHrd = () => {
        if (!hrd || !hrdToggle) return;
        const info = current.info;
        const match = info ? hrdVariants[`HRD-${String(Math.round(info.ct * 100)).padStart(4, '0')}`] : null;
        const pressed = hrdToggle.getAttribute('aria-pressed') === 'true';
        const on = pressed && Boolean(match);
        hrdToggle.disabled = !match;
        hrdToggle.setAttribute('aria-expanded', String(pressed));
        hrd.querySelector('[data-hrd-price]').textContent = match ? `+${match.price}` : '';
        hrd.querySelectorAll('[data-hrd-off]').forEach((element) => { element.hidden = on; });
        hrd.querySelectorAll('[data-hrd-on]').forEach((element) => { element.hidden = !on; });
        const note = hrd.querySelector('[data-hrd-note]');
        note.hidden = !pressed;
        note.textContent = fill(note.dataset.template, { price: match?.price || '', carat: info ? number(info.ct) : '' });
        const property = hrd.querySelector('[data-hrd-property]');
        if (property) property.disabled = !on;
        this.hrdLine = on ? { id: match.id, properties: { [hrd.dataset.hrdFor]: this.querySelector('h1')?.textContent.trim() || '' } } : null;
        const days = on ? hrd.dataset.hrdDays : '';
        if ((this.dataset.deliveryExtra || '') !== days) {
          if (days) this.dataset.deliveryExtra = days;
          else delete this.dataset.deliveryExtra;
          renderDelivery(this);
        }
      };
      hrdToggle?.addEventListener('click', () => {
        hrdToggle.setAttribute('aria-pressed', String(hrdToggle.getAttribute('aria-pressed') !== 'true'));
        syncHrd();
      }, { signal: this.controller.signal });
      const update = () => {
        const chosen = this.options
          .map((option) => (option.matches('select') ? option.selectedOptions[0] : option.querySelector('input:checked'))?.dataset.value)
          .filter(Boolean);
        const stone = chosen.find((value) => data.stones?.[value]);
        const sizes = data.stones?.[stone]?.sizes || {};
        // A stone size made only in some sizes: the others are greyed out, and a chosen size it lacks moves to the
        // nearest one it has (the variant follows on that change).
        if (sizeOption && Object.keys(sizes).length) {
          const inputs = [...sizeOption.querySelectorAll('[data-value]')];
          inputs.forEach((input) => { input.disabled = !sizes[input.dataset.value]; });
          const chosenSize = inputs.find((input) => input.matches(':checked'));
          if (chosenSize?.disabled) {
            const at = inputs.indexOf(chosenSize);
            const nearest = inputs.filter((input) => !input.disabled)
              .reduce((best, input) => (!best || Math.abs(inputs.indexOf(input) - at) < Math.abs(inputs.indexOf(best) - at) ? input : best), null);
            if (nearest) {
              if (nearest.tagName === 'OPTION') nearest.selected = true;
              else nearest.checked = true;
              (nearest.tagName === 'OPTION' ? sizeOption : nearest).dispatchEvent(new Event('change', { bubbles: true }));
              return;
            }
          }
        }
        this.querySelectorAll('.size-row-group[data-option-position]').forEach((group) => {
          const label = group.querySelector('[data-option-chosen]');
          const value = group.querySelector('input:checked')?.dataset.value;
          if (label && value) label.textContent = value;
        });
        const size = chosen.find((value) => sizes[value]);
        const info = sizes[size];
        current = { stone, size, info };
        // Each stone size's total carat at the chosen size.
        const sizeValue = sizeOption?.querySelector('[data-value]:checked')?.dataset.value;
        totalOption?.querySelectorAll('[data-value]').forEach((input) => {
          const at = data.stones?.[input.dataset.value]?.sizes?.[sizeValue];
          const label = input.closest('.chip')?.querySelector('[data-total]');
          if (label) label.textContent = at ? `${number(at.ct)} ct` : '–';
        });
        const totalChosen = totalOption?.querySelector('[data-total-chosen]');
        if (totalChosen) totalChosen.textContent = info ? `${number(info.ct)} ct` : '';
        if (totalInput) {
          totalInput.value = info ? fill(totalInput.dataset.template, { carat: number(info.ct), count: info.n }) : '';
          totalInput.disabled = !info;
        }
        if (title && info) {
          const text = fill(title.dataset.sizeTitle, { carat: number(info.ct) });
          title.textContent = text;
          if (stickyTitle) stickyTitle.textContent = text;
        }
        line.hidden = !info;
        if (info) {
          const values = { size, count: info.n, carat: number(info.ct), grams: number(info.g) };
          line.textContent = fill(data.unit === 'cm' ? line.dataset.length : line.dataset.ring, values);
        }
        if (band) {
          const caption = band.querySelector('[data-band-caption]');
          const mm = Number(data.stones?.[stone]?.mm) || 0;
          if (caption) {
            caption.textContent = info ? fill(band.dataset.caption, {
              carat: number(info.ct), count: info.n, stone: number(parseFloat(String(stone).replace(',', '.'))), mm: number(mm, 1),
            }) : '';
          }
          drawBand();
        }
        syncHrd();
      };
      update();
      placeBand();
      // The photos show one stone size, so a new stone size brings the band on the hand into view at true size, like a
      // Tektaş carat (owner 2026-10-19: the picture stayed on the first photo). On phones the band sits under the chips.
      this.addEventListener('change', (event) => {
        update();
        if (event.target.closest?.('[data-total-option]') && handSlide && !handSlide.hidden) this.showMedia('hand');
      }, { signal: this.controller.signal });
      wide.addEventListener('change', placeBand, { signal: this.controller.signal });
      const frame = band?.querySelector('.stone-hand-frame');
      if (frame && window.ResizeObserver) {
        const observer = new ResizeObserver(() => drawBand());
        observer.observe(frame);
        this.controller.signal.addEventListener('abort', () => observer.disconnect());
      }
    }
    refreshGallery() {
      this.slides = [...this.querySelectorAll('.gallery-slide:not([hidden])')];
      this.gallerySynced = false;
      this.syncGallery();
    }
    goTo(index, behavior = 'smooth') {
      if (!this.track || !this.slides.length) return;
      const target = (index + this.slides.length) % this.slides.length;
      this.track.scrollTo({ left: target * this.track.clientWidth, behavior });
    }
    // Zoom viewer: full-screen dialog; click an image to magnify, then move the mouse or drag a finger to pan.
    setupZoom() {
      const options = { signal: this.controller.signal };
      this.zoomDialog = this.querySelector('[data-zoom-dialog]');
      if (!this.zoomDialog) return;
      this.zoomTrack = this.zoomDialog.querySelector('[data-zoom-track]');
      this.zoomSlides = [...this.zoomDialog.querySelectorAll('.zoom-slide')];
      this.zoomIndex = 0;
      const zoomScale = 2.4; // .zoom-slide.is-zoomed img in joy-product.css
      let pan = null;
      let pannedAt = -Infinity;
      const step = (offset) => {
        const target = (this.zoomIndex + offset + this.zoomSlides.length) % this.zoomSlides.length;
        this.zoomTrack.scrollTo({ left: target * this.zoomTrack.clientWidth, behavior: 'smooth' });
      };
      this.zoomDialog.addEventListener('click', (event) => {
        if (event.target.closest('[data-zoom-close]')) { this.zoomDialog.close(); return; }
        if (event.target.closest('[data-zoom-prev]')) { step(-1); return; }
        if (event.target.closest('[data-zoom-next]')) { step(1); return; }
        const slide = event.target.closest('.zoom-slide');
        const image = event.target.closest('.zoom-slide img');
        if (image) {
          // The end of a finger pan is not a tap.
          if (performance.now() - pannedAt < 400) return;
          const zoomed = slide.classList.toggle('is-zoomed');
          if (zoomed) this.panZoom(image, event);
        } else if (!slide || event.target === slide) {
          this.zoomDialog.close();
        }
      }, options);
      // Touch: the photo follows the finger (the transform origin moves against the drag).
      this.zoomDialog.addEventListener('pointerdown', (event) => {
        if (!event.isPrimary) return;
        pan = null;
        const image = event.pointerType === 'touch' && event.target.closest('.zoom-slide.is-zoomed img');
        if (!image) return;
        const [originX, originY] = (image.style.transformOrigin || '50% 50%').split(' ').map(parseFloat);
        pan = { image, id: event.pointerId, x: event.clientX, y: event.clientY, originX, originY, moved: false };
      }, options);
      this.zoomDialog.addEventListener('pointermove', (event) => {
        if (pan?.id === event.pointerId) {
          const dx = event.clientX - pan.x;
          const dy = event.clientY - pan.y;
          if (!pan.moved && Math.hypot(dx, dy) < 6) return;
          pan.moved = true;
          const x = Math.max(0, Math.min(100, pan.originX - (dx / (pan.image.offsetWidth * (zoomScale - 1))) * 100));
          const y = Math.max(0, Math.min(100, pan.originY - (dy / (pan.image.offsetHeight * (zoomScale - 1))) * 100));
          pan.image.style.transformOrigin = `${x}% ${y}%`;
          return;
        }
        if (event.pointerType === 'touch') return;
        const image = event.target.closest('.zoom-slide.is-zoomed img');
        if (image) this.panZoom(image, event);
      }, options);
      const endPan = (event) => {
        if (pan?.id !== event.pointerId) return;
        if (pan.moved) pannedAt = performance.now();
        pan = null;
      };
      this.zoomDialog.addEventListener('pointerup', endPan, options);
      this.zoomDialog.addEventListener('pointercancel', endPan, options);
      this.zoomDialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') step(-1);
        if (event.key === 'ArrowRight') step(1);
      }, options);
      this.zoomTrack.addEventListener('scroll', () => {
        const index = Math.round(this.zoomTrack.scrollLeft / this.zoomTrack.clientWidth);
        if (index === this.zoomIndex) return;
        this.zoomSlides[this.zoomIndex]?.classList.remove('is-zoomed');
        this.zoomIndex = index;
        this.loadZoomImage(index);
        this.loadZoomImage(index + 1);
        const counter = this.zoomDialog.querySelector('[data-zoom-counter]');
        if (counter) counter.textContent = `${index + 1} / ${this.zoomSlides.length}`;
      }, options);
      this.zoomDialog.addEventListener('close', () => {
        this.zoomSlides.forEach((slide) => slide.classList.remove('is-zoomed'));
        this.goTo(this.zoomIndex, 'instant');
      }, options);
    }
    loadZoomImage(index) {
      const image = this.zoomSlides[index]?.querySelector('img[data-zoom-src]');
      if (image && !image.src) image.src = image.dataset.zoomSrc;
    }
    panZoom(image, event) {
      const box = image.getBoundingClientRect();
      const x = Math.max(0, Math.min(100, ((event.clientX - box.left) / box.width) * 100));
      const y = Math.max(0, Math.min(100, ((event.clientY - box.top) / box.height) * 100));
      image.style.transformOrigin = `${x}% ${y}%`;
    }
    openZoom(index) {
      if (!this.zoomDialog) return;
      this.loadZoomImage(index);
      this.loadZoomImage(index + 1);
      this.zoomDialog.showModal();
      this.zoomIndex = index;
      this.zoomTrack.scrollTo({ left: index * this.zoomTrack.clientWidth, behavior: 'instant' });
      const counter = this.zoomDialog.querySelector('[data-zoom-counter]');
      if (counter) counter.textContent = `${index + 1} / ${this.zoomSlides.length}`;
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.variantController?.abort();
      this.observer?.disconnect();
      this.informationObserver?.disconnect();
    }
    // Two-column layout: the options column sticks beside the photos. A column taller than the window sticks by
    // its bottom edge instead, so every option and "Sepete Ekle" can still be scrolled into view.
    setupStickyInformation() {
      const information = this.querySelector('.product-information');
      if (!information || !('ResizeObserver' in window)) return;
      const place = () => {
        // 24px below the sticky header (joy.js sets --header-height).
        const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
        information.style.setProperty('--information-top', `${Math.min(header + 24, window.innerHeight - information.offsetHeight - 24)}px`);
      };
      this.informationObserver?.disconnect();
      this.informationObserver = new ResizeObserver(place);
      this.informationObserver.observe(information);
      window.addEventListener('resize', place, { signal: this.controller.signal });
    }
    // The approval in the order confirmation; without it (approval off in the theme settings) "Hemen Al" needs none.
    consentBox() {
      return this.querySelector('[data-buy-now-consent] input[type="checkbox"]');
    }
    syncPurchase() {
      const result = this.querySelector('[data-variant-result]');
      const purchasable = Boolean(result?.dataset.variantId) && result?.dataset.available === 'true';
      const ready = purchasable && !this.resolving && !this.adding;
      const input = this.form?.querySelector('[data-variant-input]');
      if (input) input.disabled = !ready;
      const sticky = this.querySelector('[data-sticky-submit]');
      if (sticky) sticky.disabled = !ready;
      const main = this.querySelector('[data-product-submit]');
      if (main) main.disabled = !ready;
      this.querySelectorAll('[data-buy-now]').forEach((button) => { button.disabled = !ready; });
      const price = this.querySelector('[data-main-price]');
      if (price) this.querySelector('[data-sticky-price]').textContent = price.textContent.trim();
      // The bar stays up while an option is checked or the item is added, so it never flickers away
      // under the pressed button; its buttons are disabled meanwhile.
      const bar = this.querySelector('[data-sticky-purchase]');
      if (bar) bar.hidden = !purchasable || !this.purchaseOffscreen;
      // The floating WhatsApp button steps aside until the customer has scrolled past the main button: the options and
      // buy buttons pass under its corner, and the product has its own WhatsApp button (adviser 2026-10-19).
      if (main && document.body.dataset.template === 'product' && document.querySelector('joy-product') === this) {
        document.documentElement.classList.toggle('purchase-in-view', !this.purchaseOffscreen);
      }
    }
    observePurchase() {
      this.observer?.disconnect();
      const button = this.querySelector('[data-product-submit]');
      if (!button || !('IntersectionObserver' in window)) return;
      // Only after the customer has scrolled past the main button (owner 2026-10-02): not while the options above it
      // are still being chosen.
      this.observer = new IntersectionObserver((entries) => {
        const entry = entries[entries.length - 1];
        this.purchaseOffscreen = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        this.syncPurchase();
      });
      this.observer.observe(button);
    }
    releasePurchase() {
      clearTimeout(this.leaveTimer);
      if (!this.restorePurchase) return;
      this.restorePurchase();
      this.restorePurchase = null;
    }
    // Unticked approval in the order confirmation: no add; show its error and focus the box.
    requireConsent() {
      const wrap = this.querySelector('[data-buy-now-consent] [data-cart-consent]');
      if (!wrap) return;
      wrap.classList.add('is-invalid');
      const error = wrap.querySelector('[data-consent-error]');
      if (error) {
        error.hidden = false;
        announce(error.textContent.trim());
      }
      wrap.querySelector('input[type="checkbox"]')?.focus();
    }
    // "Hemen Al": the piece, its price, the withdrawal exception and the approval in one sheet before checkout. An entered
    // engraving makes the piece made to order, so its exception line shows too. False where <dialog> is missing.
    openSheet() {
      const sheet = this.querySelector('[data-buy-now-sheet]');
      if (!sheet || typeof sheet.showModal !== 'function') return false;
      const engraving = this.form.querySelector('[data-engrave-text-input]');
      const engraved = (engraving && !engraving.disabled && engraving.value.trim()) || this.alyansEngraved?.();
      const personal = sheet.querySelector('[data-sheet-personal]');
      if (personal) personal.hidden = personal.dataset.personal !== 'true' && !engraved;
      // The 14-day line (withdrawal terms right before payment) gives way when the piece has no withdrawal right.
      const withdrawal = sheet.querySelector('[data-sheet-withdrawal]');
      if (withdrawal && personal) withdrawal.hidden = !personal.hidden;
      // The price as the page shows it now: wedding bands build the pair price in the page and never re-render this
      // sheet, whose price and options were printed for the first ring (joy-alyans.js lists each ring below instead).
      const sheetPrice = sheet.querySelector('.buy-now-sheet-price');
      const pagePrice = this.querySelector('[data-main-price]');
      if (sheetPrice && pagePrice) sheetPrice.textContent = pagePrice.textContent.trim();
      const sheetOptions = sheet.querySelector('[data-sheet-options]');
      if (sheetOptions) sheetOptions.hidden = Boolean(this.alyansSummary);
      // The choices the order will carry (total carat, gold colour, engraving …); "_" names are Shopify's hidden ones.
      const props = sheet.querySelector('[data-sheet-properties]');
      if (props) {
        // Wedding bands list each ring (snippets/joy-alyans.liquid). The HRD report line carries its price, which
        // checkout adds on its own line.
        const hrdPrice = this.hrdLine ? this.querySelector('[data-hrd-price]')?.textContent.trim() : '';
        const lines = this.alyansSummary ? this.alyansSummary() : [...this.form.querySelectorAll('[name^="properties["]')]
          .filter((input) => !input.disabled && (!/radio|checkbox/.test(input.type) || input.checked) && String(input.value).trim())
          .map((input) => [input.name.slice(11, -1), String(input.value).trim() + (hrdPrice && input.matches('[data-hrd-property]') ? ` · ${hrdPrice}` : '')])
          .filter(([name]) => name && !name.startsWith('_'));
        props.replaceChildren(...lines.map(([name, value]) => {
          const line = document.createElement('span');
          line.textContent = `${name}: ${value}`;
          return line;
        }));
        props.hidden = !lines.length;
      }
      // Checkout takes the whole bag: say so when it already holds other pieces.
      const cartNote = sheet.querySelector('[data-sheet-cart]');
      if (cartNote) cartNote.hidden = !(Number(document.querySelector('[data-cart-count]')?.textContent) > 0);
      // The approval is asked afresh every time the confirmation opens (never shown ticked from an earlier tap).
      const box = this.consentBox();
      if (box) {
        box.checked = false;
        const wrap = box.closest('[data-cart-consent]');
        wrap?.classList.remove('is-invalid');
        const boxError = wrap?.querySelector('[data-consent-error]');
        if (boxError) boxError.hidden = true;
      }
      if (!sheet.open) sheet.showModal();
      box?.focus();
      return true;
    }
    // Tektaş: only the chosen cut's "Model" row is shown and sent (cuts with one design have none).
    syncModels() {
      const shape = this.querySelector('[data-shape-option] input:checked')?.dataset.value;
      this.querySelectorAll('[data-model-group]').forEach((group) => {
        const active = group.dataset.modelShape === shape;
        group.hidden = !active;
        group.querySelectorAll('input').forEach((input) => { input.disabled = !active; });
      });
    }
    // The first photo whose alt text names the gold colour. On Tektaş products the shape, design and carat count
    // too ("Yuvarlak · 6 Tırnak · Beyaz Altın", "Oval · 1,00 ct · Beyaz Altın"); the closest match wins: shape, design
    // and colour, shape and design, then all three of shape, carat and colour, shape and colour, shape and carat,
    // shape, colour.
    showColour(name = this.querySelector('[data-colour-property] input:checked')?.value || '') {
      const find = (...words) => (words.every(Boolean) ? (this.slides || []).findIndex((slide) => {
        const alt = (slide.querySelector('img')?.alt || '').toLocaleLowerCase('tr');
        return words.every((word) => alt.includes(word.toLocaleLowerCase('tr')));
      }) : -1);
      const shape = this.querySelector('[data-shape-option] input:checked')?.dataset.value;
      const carat = this.querySelector('[data-stone-option] .stone-chip input:checked')?.dataset.value.split('·')[0].trim();
      const model = this.querySelector('[data-model-group]:not([hidden]) input:checked')?.value;
      const designs = shape && model ? [[shape, model, name], [shape, model]] : [];
      const tries = shape ? [...designs, [shape, carat, name], [shape, name], [shape, carat], [shape], [name]] : [[name]];
      for (const words of tries) {
        const index = find(...words);
        if (index >= 0) { this.goTo(index); return; }
      }
    }
    showMedia(id, behavior = 'smooth') {
      const index = (this.slides || []).findIndex((slide) => slide.dataset.media === id);
      if (index >= 0) this.goTo(index, behavior);
    }
    async resolveVariant() {
      if (this.adding) return;
      this.variantController?.abort();
      const requestNumber = ++this.requestNumber;
      const error = this.querySelector('[data-product-error]');
      error.hidden = true;
      this.querySelector('[data-retry-variant]')?.remove();
      this.resolving = true;
      this.syncPurchase();
      const values = this.options.map((option) => (option.matches('select') ? option.value : option.querySelector('input:checked')?.value || ''));
      if (values.some((value) => !value)) {
        const result = this.querySelector('[data-variant-result]');
        result.dataset.variantId = '';
        result.dataset.available = 'false';
        this.querySelector('[data-availability]').textContent = T.choose_all_options;
        this.querySelector('[data-product-submit]').textContent = T.choose_options;
        const price = this.querySelector('[data-product-price]');
        price.textContent = T.choose_options;
        price.removeAttribute('aria-busy');
        const pageUrl = new URL(location.href);
        pageUrl.searchParams.delete('variant');
        pageUrl.searchParams.delete('option_values');
        history.replaceState({}, '', pageUrl);
        this.resolving = false;
        this.syncPurchase();
        return;
      }
      this.variantController = new AbortController();
      const controller = this.variantController;
      const timeout = setTimeout(() => controller.abort(), 15000);
      const url = new URL(this.dataset.productUrl, location.origin);
      url.searchParams.set('option_values', values.join(','));
      // The small variant section (price + variant block), not the whole product section.
      url.searchParams.set('section_id', 'joy-product-variant');
      this.querySelector('[data-product-price]')?.setAttribute('aria-busy', 'true');
      announce(T.checking_option);
      try {
        const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw new Error(T.option_error);
        const documentFragment = parsed(await response.text());
        if (requestNumber !== this.requestNumber) return;
        const result = documentFragment.querySelector('[data-variant-result]');
        const price = documentFragment.querySelector('[data-product-price]');
        if (!result || !price) throw new Error(T.option_error);
        // The new block's approval follows the customer's tick (its save on the cart may still be on its way).
        const ticked = this.consentBox()?.checked;
        this.querySelector('[data-variant-result]').replaceWith(result);
        const newConsent = this.consentBox();
        if (newConsent && ticked !== undefined) newConsent.checked = ticked;
        renderDelivery(this);
        this.querySelector('[data-product-price]').replaceWith(price);
        // Shop Pay Installments line (US store): Shopify's banner follows its form's variant id on "change".
        const installments = this.querySelector('[data-installments-variant]');
        if (installments && result.dataset.variantId && installments.value !== result.dataset.variantId) {
          installments.value = result.dataset.variantId;
          installments.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (!result.dataset.variantId) this.querySelector('[data-availability]').textContent = T.option_unavailable_combo;
        this.showMedia(result.dataset.mediaId);
        const pageUrl = new URL(location.href);
        pageUrl.searchParams.delete('option_values');
        if (result.dataset.variantId) pageUrl.searchParams.set('variant', result.dataset.variantId);
        else { pageUrl.searchParams.delete('variant'); pageUrl.searchParams.set('option_values', values.join(',')); }
        history.replaceState({}, '', pageUrl);
        announce(result.dataset.available === 'true' ? T.option_updated : T.option_not_available);
      } catch (failure) {
        if (requestNumber !== this.requestNumber) return;
        const result = this.querySelector('[data-variant-result]');
        result.dataset.available = 'false';
        error.textContent = T.option_check_failed;
        error.hidden = false;
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'text-link';
        retry.dataset.retryVariant = '';
        retry.textContent = T.retry;
        error.after(retry);
      } finally {
        clearTimeout(timeout);
        if (requestNumber === this.requestNumber) {
          this.resolving = false;
          this.querySelector('[data-product-price]')?.removeAttribute('aria-busy');
          this.syncPurchase();
          this.observePurchase();
        }
      }
    }
    async addToCart(event) {
      event.preventDefault();
      if (this.resolving || this.adding) return;
      // "Hemen Al" adds and goes straight to checkout: with the approval on it first opens the order confirmation,
      // whose "Ödemeye Geç" (data-sheet-confirm) continues here once the customer has ticked the approval.
      const buyNow = Boolean(event.submitter?.hasAttribute('data-buy-now'));
      const consent = this.consentBox();
      if (buyNow && consent && !event.submitter.hasAttribute('data-sheet-confirm')) {
        if (!this.form.reportValidity()) return;
        const chosen = this.querySelector('[data-variant-result]');
        if (!chosen?.dataset.variantId || chosen.dataset.available !== 'true') return;
        if (this.openSheet()) return;
      }
      if (buyNow && consent && !consent.checked) {
        this.requireConsent();
        return;
      }
      if (!this.form.reportValidity()) return;
      const result = this.querySelector('[data-variant-result]');
      if (!result?.dataset.variantId || result.dataset.available !== 'true') return;
      if (cart.busy) {
        this.querySelector('[data-buy-now-sheet]')?.close();
        const error = this.querySelector('[data-product-error]');
        error.textContent = T.cart_busy;
        error.hidden = false;
        return;
      }
      const formData = new FormData(this.form);
      [...formData.keys()].filter((key) => key.startsWith('joy-option-')).forEach((key) => formData.delete(key));
      formData.set('id', result.dataset.variantId);
      formData.set('sections', 'joy-cart-drawer');
      formData.set('sections_url', root + 'cart');
      // A piece with an HRD report: its line and the report's carry the same hidden token, so the cart keeps them
      // together (snippets/joy-cart-links.liquid); a unique token also keeps the piece on a line of its own.
      const hrdToken = this.hrdLine ? `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}` : '';
      if (hrdToken) formData.set('properties[_hrd]', hrdToken);
      // Wedding bands: the chosen rings go in together, each on its own line (snippets/joy-alyans.liquid).
      const alyansItems = this.alyansItems?.();
      this.adding = true;
      cart.busy = true;
      this.options.forEach((option) => { option.disabled = true; });
      this.syncPurchase();
      const error = this.querySelector('[data-product-error]');
      error.hidden = true;
      const button = buyNow ? event.submitter : this.querySelector('[data-product-submit]');
      // Child nodes, not text, so the bag icon comes back with the label.
      const originalLabel = [...button.childNodes];
      button.textContent = T.adding;
      let consentLocked = false;
      const finish = () => {
        this.adding = false;
        cart.busy = false;
        this.options.forEach((option) => { option.disabled = false; });
        if (consentLocked) document.querySelectorAll(consentSelector).forEach((box) => { box.disabled = false; });
        button.replaceChildren(...originalLabel);
        this.syncPurchase();
      };
      let leaving = false;
      try {
        let data = await request(root + 'cart/add.js', alyansItems
          ? { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: alyansItems, sections: ['joy-cart-drawer'], sections_url: root + 'cart' }) }
          : { method: 'POST', headers: { Accept: 'application/json' }, body: formData });
        const pieceKey = data.key;
        let limited = false;
        // "Hemen Al" buys the quantity chosen now: Shopify merges it into the same piece already in the bag, so set
        // that line back (a bag holding 1 + "Hemen Al" 1 would otherwise reach checkout as 2).
        const wanted = buyNow ? Math.min(Number(formData.get('quantity')) || 1, maxQuantity) : maxQuantity;
        if (Number(data.quantity) > wanted) {
          data = await request(root + 'cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: data.key, quantity: wanted, sections: ['joy-cart-drawer'], sections_url: root + 'cart' })
          });
          limited = !buyNow;
        }
        // The HRD report (a service) goes in as its own line right after the piece, with the piece's quantity. If it
        // fails, the piece comes out again (it would claim a report nobody paid for) and nothing goes to checkout.
        if (this.hrdLine) {
          try {
            data = await request(root + 'cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: [{ id: this.hrdLine.id, quantity: Number(formData.get('quantity')) || 1, properties: { ...this.hrdLine.properties, _hrd_for: hrdToken } }], sections: ['joy-cart-drawer'], sections_url: root + 'cart' })
            });
          } catch (failure) {
            try {
              const rolledBack = await request(root + 'cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ id: pieceKey, quantity: 0, sections: ['joy-cart-drawer'], sections_url: root + 'cart' })
              });
              updateCart(rolledBack, false);
            } catch (rollbackFailure) { /* the cart drawer tidies the piece's HRD line on the next page */ }
            throw new Error(`${T.hrd_failed || ''} ${failure.message || ''}`.trim() || T.generic_error);
          }
        }
        // "Hemen Al" refreshes the drawer silently and goes on to checkout; if the approval was unticked
        // while adding, the drawer opens as for "Sepete Ekle".
        // A store that asks for the approval but has no confirmation sheet here (a wedding band opened on a sold-out
        // ring) opens the drawer, where the approval is, instead of going straight to checkout.
        const checkout = buyNow && (consent ? consent.checked : !window.JoySettings?.cartConsent);
        updateCart(data, !checkout);
        // The re-rendered drawer copy follows the page (an approval save may still be on its way).
        if (consent) syncConsent(consent);
        if (limited) {
          const notice = document.querySelector('joy-cart [data-cart-error]');
          if (notice) { notice.textContent = String(T.max_quantity || '').replace('[count]', maxQuantity); notice.hidden = false; }
        }
        if (checkout) {
          // The approval must be on the cart (and so on the order) before checkout opens. Until the page
          // leaves it cannot be unticked, so no withdrawal races this save.
          if (consent) {
            consentLocked = true;
            document.querySelectorAll(consentSelector).forEach((box) => { box.disabled = true; });
            await request(root + 'cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ attributes: { [consent.dataset.consentLabel]: consent.value } })
            });
          }
          leaving = true;
          location.assign(root + 'checkout');
        }
      } catch (failure) {
        // The message sits under the buttons, behind the order confirmation: close it first.
        this.querySelector('[data-buy-now-sheet]')?.close();
        error.textContent = failure instanceof ConnectionError ? T.add_connection : failure.message;
        error.hidden = false;
      } finally {
        // On the way to checkout stay busy, so a second tap cannot add the item again. If the page never
        // leaves (checkout load stopped with Esc), release after a while.
        if (leaving) {
          this.restorePurchase = finish;
          this.leaveTimer = setTimeout(() => this.releasePurchase(), 8000);
        } else {
          finish();
        }
      }
    }
  }
  if (!customElements.get('joy-product')) customElements.define('joy-product', JoyProduct);

  // Ring size row: one line of sizes with arrows; the chosen size is kept in view.
  const setupSizeRows = (scope = document) => scope.querySelectorAll('[data-size-row]').forEach((row) => {
    const track = row.querySelector('[data-size-row-track]');
    const previous = row.querySelector('[data-size-row-step="-1"]');
    const next = row.querySelector('[data-size-row-step="1"]');
    const update = () => {
      const overflow = track.scrollWidth > track.clientWidth + 2;
      previous.hidden = !overflow || track.scrollLeft <= 2;
      next.hidden = !overflow || track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    };
    const center = (chip, smooth) => {
      if (!chip) return;
      track.scrollTo({ left: chip.offsetLeft - (track.clientWidth - chip.offsetWidth) / 2, behavior: smooth ? 'smooth' : 'auto' });
    };
    row.querySelectorAll('[data-size-row-step]').forEach((button) => button.addEventListener('click', () => {
      track.scrollBy({ left: Number(button.dataset.sizeRowStep) * track.clientWidth * 0.6, behavior: 'smooth' });
    }));
    track.addEventListener('scroll', update, { passive: true });
    track.addEventListener('change', (event) => center(event.target.closest('.chip'), true));
    // Desktop: drag the row with the mouse like a slider; a drag never selects a size. On release the row keeps
    // gliding and slows down like a phone's scroll, then settles on the nearest size (snapping stays off until then).
    let drag = null;
    let dragged = false;
    let glide = 0;
    const settle = () => {
      const middle = track.scrollLeft + track.clientWidth / 2;
      const distance = (chip) => Math.abs(chip.offsetLeft + chip.offsetWidth / 2 - middle);
      const nearest = [...track.querySelectorAll('.chip')].reduce((best, chip) => (!best || distance(chip) < distance(best) ? chip : best), null);
      center(nearest, true);
      setTimeout(() => track.classList.remove('is-dragging'), 350);
    };
    track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      cancelAnimationFrame(glide);
      drag = { x: event.clientX, left: track.scrollLeft, samples: [{ x: event.clientX, t: event.timeStamp }] };
      dragged = false;
    });
    window.addEventListener('pointermove', (event) => {
      if (!drag) return;
      const distance = event.clientX - drag.x;
      drag.samples.push({ x: event.clientX, t: event.timeStamp });
      if (drag.samples.length > 6) drag.samples.shift();
      if (!dragged && Math.abs(distance) < 5) return;
      dragged = true;
      track.classList.add('is-dragging');
      track.scrollLeft = drag.left - distance;
    });
    window.addEventListener('pointerup', (event) => {
      if (!drag) return;
      const samples = drag.samples.filter((sample) => event.timeStamp - sample.t < 100);
      drag = null;
      if (!dragged) {
        track.classList.remove('is-dragging');
        return;
      }
      // Speed of the last ~100 ms of the drag, in px per ms (positive = towards the larger sizes).
      const first = samples[0];
      const last = samples[samples.length - 1];
      let velocity = samples.length > 1 ? (first.x - last.x) / Math.max(1, last.t - first.t) : 0;
      if (Math.abs(velocity) < 0.1 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        settle();
        return;
      }
      let before = performance.now();
      const step = (now) => {
        const elapsed = Math.min(32, now - before);
        before = now;
        track.scrollLeft += velocity * elapsed;
        velocity *= 0.95 ** (elapsed / 16);
        const atEdge = track.scrollLeft <= 0 || track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
        if (Math.abs(velocity) > 0.02 && !atEdge) glide = requestAnimationFrame(step);
        else settle();
      };
      glide = requestAnimationFrame(step);
    });
    track.addEventListener('click', (event) => {
      if (!dragged) return;
      event.preventDefault();
      event.stopPropagation();
      dragged = false;
    }, true);
    // A new width (rotation, window size) re-centres the chosen size; a phone's toolbar changing the height does not.
    let lastWidth = window.innerWidth;
    window.addEventListener('resize', () => {
      update();
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      center(track.querySelector('input:checked')?.closest('.chip'), false);
    });
    center(track.querySelector('input:checked')?.closest('.chip'), false);
    update();
  });
  setupSizeRows();
  document.addEventListener('shopify:section:load', (event) => setupSizeRows(event.target));

  // Free engraving (products tagged "Gravür" or "Engraving"): drawer with a live preview in the chosen gold colour.
  const engraveMetal = (product) => {
    const value = (product.querySelector('[data-colour-property] input:checked')?.value || '').toLocaleLowerCase('tr');
    if (value.includes('beyaz') || value.includes('white')) return 'white';
    if (value.includes('pembe') || value.includes('rose') || value.includes('pink')) return 'rose';
    return 'yellow';
  };
  const updateEngravePreview = (dialog) => {
    const field = dialog.querySelector('[data-engrave-field]');
    const script = dialog.querySelector('[data-font]:checked')?.dataset.font !== 'block';
    dialog.querySelectorAll('[data-engrave-preview]').forEach((preview) => {
      preview.textContent = field.value.trim() || preview.dataset.default;
      preview.classList.toggle('is-script', script);
    });
    // Long text shrinks so it stays on the curve (about 620 units of the 1000-wide preview).
    const curveText = dialog.querySelector('.engrave-photo-text');
    if (curveText?.getComputedTextLength) {
      curveText.style.fontSize = '';
      const inner = curveText.querySelector('textPath');
      if (inner) inner.style.fontSize = '';
      const length = curveText.getComputedTextLength();
      if (length > 620 && inner) inner.style.fontSize = `${parseFloat(getComputedStyle(inner).fontSize) * 620 / length}px`;
    }
    dialog.querySelector('[data-engrave-count]').textContent = `${field.value.length}/${field.maxLength}`;
  };
  const setEngraving = (box, text, fontInput) => {
    const textInput = box.querySelector('[data-engrave-text-input]');
    const fontField = box.querySelector('[data-engrave-font-input]');
    textInput.value = text;
    textInput.disabled = !text;
    fontField.value = text && fontInput ? fontInput.value : '';
    fontField.dataset.font = fontInput?.dataset.font || 'script';
    fontField.disabled = !text;
    box.querySelector('[data-engrave-summary]').hidden = !text;
    box.querySelector('.engrave-add').hidden = Boolean(text);
    box.querySelector('[data-engrave-summary-text]').textContent = text ? `“${text}” · ${fontField.value}` : '';
    // The Tektaş "Seçiminiz" box lists the engraving too (joy-tektas.js).
    box.dispatchEvent(new CustomEvent('joy:engraving', { bubbles: true }));
  };
  document.addEventListener('click', (event) => {
    const open = event.target.closest('[data-engrave-open]');
    if (open) {
      const product = open.closest('joy-product');
      const dialog = product?.querySelector('[data-engrave-dialog]');
      // The box the button belongs to (wedding bands have one per ring, snippets/joy-alyans.liquid), else the page's one.
      const box = open.closest('[data-engrave]') || product?.querySelector('[data-engrave]');
      if (!dialog || !box) return;
      dialog.engraveBox = box;
      dialog.querySelector('[data-engrave-field]').value = box.querySelector('[data-engrave-text-input]').value;
      const font = box.querySelector('[data-engrave-font-input]').dataset.font || 'script';
      const fontInput = dialog.querySelector(`[data-font="${font}"]`);
      if (fontInput) fontInput.checked = true;
      dialog.querySelector('[data-engrave-band]').dataset.metal = engraveMetal(product);
      dialog.showModal();
      updateEngravePreview(dialog);
      // Touch screens: no keyboard over the preview until the customer taps the field.
      if (!window.matchMedia('(pointer: coarse)').matches) dialog.querySelector('[data-engrave-field]').focus();
      return;
    }
    if (event.target.matches?.('[data-engrave-dialog]') || event.target.closest('[data-engrave-close]')) {
      (event.target.closest('[data-engrave-dialog]') || event.target).close();
      return;
    }
    const save = event.target.closest('[data-engrave-save]');
    if (save) {
      const dialog = save.closest('[data-engrave-dialog]');
      const box = dialog.engraveBox || dialog.closest('joy-product').querySelector('[data-engrave]');
      const text = dialog.querySelector('[data-engrave-field]').value.trim();
      setEngraving(box, text, dialog.querySelector('[data-font]:checked'));
      dialog.close();
      if (text) announce(T.engraving_saved);
      box.querySelector(text ? '[data-engrave-summary] [data-engrave-open]' : '.engrave-add')?.focus();
      return;
    }
    const remove = event.target.closest('[data-engrave-remove]');
    if (remove) {
      const box = remove.closest('[data-engrave]');
      setEngraving(box, '', null);
      box.querySelector('.engrave-add').focus();
    }
  });
  ['input', 'change'].forEach((type) => document.addEventListener(type, (event) => {
    const dialog = event.target.closest?.('[data-engrave-dialog]');
    if (dialog) updateEngravePreview(dialog);
  }));

  // Estimated delivery: Istanbul time, weekdays only, same-day shipping before the cut-off for stock pieces.
  function istanbulNow() {
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Istanbul', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
    }).formatToParts(new Date()).map((part) => [part.type, part.value]));
    return { date: new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day))), minutes: Number(parts.hour) * 60 + Number(parts.minute) };
  }
  function renderDelivery(scope = document) {
    scope.querySelectorAll('[data-delivery-estimate]').forEach((element) => {
    // Settings from the product section (data-delivery-* on <joy-product>); none when the estimate is switched off.
    const settings = element.closest('joy-product')?.dataset || {};
    if (!settings.deliveryCutoff) { element.hidden = true; return; }
    const holidays = new Set((settings.deliveryHolidays || '').split(',').map((value) => value.trim()).filter(Boolean));
    const isWorkday = (date) => date.getUTCDay() !== 0 && date.getUTCDay() !== 6 && !holidays.has(date.toISOString().slice(0, 10));
    const addWorkdays = (date, count) => {
      const result = new Date(date);
      let added = 0;
      while (added < count) {
        result.setUTCDate(result.getUTCDate() + 1);
        if (isWorkday(result)) added += 1;
      }
      return result;
    };
    const now = istanbulNow();
    const cutoff = Number(settings.deliveryCutoff || 14) * 60;
    // Every piece is finished for the order: a production range (data-delivery-made-min … data-delivery-made-days,
    // per product) comes before dispatch.
    // An added service (the HRD report: data-delivery-extra) adds its business days before dispatch.
    const extra = Number(settings.deliveryExtra || 0);
    const madeMax = Number(settings.deliveryMadeDays || 0);
    const prepMax = madeMax + extra;
    const prepMin = Math.min(Number(settings.deliveryMadeMin || madeMax), madeMax) + extra;
    const prep = prepMax;
    const shipsToday = isWorkday(now.date) && now.minutes < cutoff;
    const dispatch = shipsToday ? now.date : addWorkdays(now.date, 1);
    const dispatchFrom = prepMin > 0 ? addWorkdays(dispatch, prepMin) : dispatch;
    const dispatchTo = prepMax > 0 ? addWorkdays(dispatch, prepMax) : dispatch;
    const from = addWorkdays(dispatchFrom, Number(settings.deliveryTransitMin || 1));
    const to = addWorkdays(dispatchTo, Math.max(Number(settings.deliveryTransitMax || 2), Number(settings.deliveryTransitMin || 1)));
    const format = (date) => date.toLocaleDateString(document.documentElement.lang || 'tr', { day: 'numeric', month: 'long', weekday: 'long', timeZone: 'UTC' });
    const range = from.getTime() === to.getTime() ? format(from) : `${format(from)} – ${format(to)}`;
    const text = element.querySelector('[data-delivery-text]');
    text.replaceChildren();
    if (prep === 0 && shipsToday) {
      const left = cutoff - now.minutes;
      const time = left >= 60
        ? T.delivery_hours.replace('[h]', Math.floor(left / 60)).replace('[m]', left % 60)
        : T.delivery_minutes.replace('[m]', left);
      const lead = document.createElement('span');
      lead.className = 'delivery-countdown';
      lead.textContent = T.delivery_today.replace('[time]', time);
      text.append(lead);
    }
    const estimate = document.createElement('span');
    estimate.textContent = T.delivery_estimate.replace('[date]', range);
    text.append(estimate);
    element.hidden = false;
    });
  }
  renderDelivery();
  setInterval(() => renderDelivery(), 60000);
})();
